/**
 * Input for a top-down tank: WASD/arrows drive, the mouse aims the turret (no
 * pointer lock — the cursor is the crosshair), left mouse fires, Space (or right
 * mouse) lays a mine. Movement keys are polled; fire/mine are edge-or-held.
 */

export class Input {
  private keys = new Set<string>();
  private justPressed = new Set<string>();
  /** mouse position in client pixels */
  mouseX = window.innerWidth / 2;
  mouseY = window.innerHeight / 2;
  fireHeld = false;
  private mineQueued = false;
  private wheelAccum = 0; // accumulated zoom notches (consume each frame)

  constructor(private canvas: HTMLElement) {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("mousedown", this.onMouseDown);
    window.addEventListener("mouseup", this.onMouseUp);
    window.addEventListener("wheel", this.onWheel, { passive: false });
    // suppress the context menu so right-click can lay mines
    this.canvas.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  private onWheel = (e: WheelEvent) => {
    e.preventDefault();
    this.wheelAccum += Math.sign(e.deltaY);
  };

  /** Consume accumulated wheel notches (positive = scrolled down = zoom out). */
  consumeWheel(): number {
    const v = this.wheelAccum;
    this.wheelAccum = 0;
    return v;
  }

  private onKeyDown = (e: KeyboardEvent) => {
    if (["Space", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.code)) e.preventDefault();
    if (!e.repeat) {
      this.justPressed.add(e.code);
      if (e.code === "Space") this.mineQueued = true;
    }
    this.keys.add(e.code);
  };
  private onKeyUp = (e: KeyboardEvent) => {
    this.keys.delete(e.code);
  };
  private onMouseMove = (e: MouseEvent) => {
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;
  };
  private onMouseDown = (e: MouseEvent) => {
    if (e.button === 0) {
      // A left-click on an interactive HUD control (card hand, menu/pause/shop buttons,
      // volume slider, links) must NOT arm fire/placement — only canvas clicks should.
      // The HUD root is pointer-events:none, so only pointer-events:auto controls are the
      // target. (Fixes card-bar clicks leaking into a ground placement underneath the UI.)
      const t = e.target as HTMLElement | null;
      if (t && t.closest("button,input,a,select,textarea,label")) return;
      this.fireHeld = true;
    }
    if (e.button === 2) this.mineQueued = true;
  };
  private onMouseUp = (e: MouseEvent) => {
    if (e.button === 0) this.fireHeld = false;
  };

  isDown(code: string): boolean {
    return this.keys.has(code);
  }

  /** Movement axis (x = strafe/east, z = forward/north is -z). */
  moveAxis(): { x: number; z: number } {
    let x = 0, z = 0;
    if (this.isDown("KeyW") || this.isDown("ArrowUp")) z -= 1;
    if (this.isDown("KeyS") || this.isDown("ArrowDown")) z += 1;
    if (this.isDown("KeyA") || this.isDown("ArrowLeft")) x -= 1;
    if (this.isDown("KeyD") || this.isDown("ArrowRight")) x += 1;
    return { x, z };
  }

  /** Mouse position as normalized device coords (-1..1, y up). */
  ndc(): { x: number; y: number } {
    return {
      x: (this.mouseX / window.innerWidth) * 2 - 1,
      y: -(this.mouseY / window.innerHeight) * 2 + 1,
    };
  }

  /** True once if a mine was requested since last check. */
  consumeMine(): boolean {
    const v = this.mineQueued;
    this.mineQueued = false;
    return v;
  }

  consumeKeyPress(code: string): boolean {
    if (this.justPressed.has(code)) {
      this.justPressed.delete(code);
      return true;
    }
    return false;
  }

  endFrame(): void {
    this.justPressed.clear();
  }
}
